import { RootState } from 'app/AppStore';
import { GarageLimitOverride, SearchRateLimitConfig, SearchRateLimitState } from './SearchRateLimit.types';

/**
 * Redux selectors for search rate limit configuration state.
 */

/**
 * Select the entire search rate limit state.
 */
export const selectSearchRateLimitState = (state: RootState): SearchRateLimitState => state.searchRateLimit;

/**
 * Select the current rate limit configuration.
 */
export const selectSearchRateLimitConfig = (state: RootState): SearchRateLimitConfig | null =>
  state.searchRateLimit.config;

/**
 * Select loading state (initial config fetch).
 */
export const selectSearchRateLimitLoading = (state: RootState): boolean => state.searchRateLimit.loading;

/**
 * Select error message.
 */
export const selectSearchRateLimitError = (state: RootState): string | null => state.searchRateLimit.error;

/**
 * Select if config is dirty (has unsaved changes).
 */
export const selectSearchRateLimitIsDirty = (state: RootState): boolean => state.searchRateLimit.isDirty;

/**
 * Select last update timestamp.
 */
export const selectSearchRateLimitLastUpdated = (state: RootState): string | null => state.searchRateLimit.lastUpdated;

/**
 * Select rate limit config for a specific country.
 */
export const selectSearchRateLimitByCountry = (countryCode: string) => (
  state: RootState,
): SearchRateLimitConfig | null => {
  const config = state.searchRateLimit.config;
  return config?.countryCode === countryCode ? config : null;
};

/**
 * Select effective short-term limit for a garage.
 */
export const selectEffectiveShortTermLimit = (garageId: string, countryCode: string) => (
  state: RootState,
): number | null => {
  const config = state.searchRateLimit?.config;
  if (config?.countryCode !== countryCode) {
    return null;
  }

  const override = config.garageOverrides?.[garageId];
  return override?.shortTermLimit ?? config.shortTermLimit;
};

/**
 * Select effective long-term limit for a garage.
 */
export const selectEffectiveLongTermLimit = (garageId: string, countryCode: string) => (
  state: RootState,
): number | null => {
  const config = state.searchRateLimit?.config;
  if (config?.countryCode !== countryCode) {
    return null;
  }

  const override = config.garageOverrides?.[garageId];
  return override?.longTermLimit ?? config.longTermLimit;
};

/**
 * Select all garage overrides for a country.
 */
export const selectGarageOverrides = (countryCode: string) => (
  state: RootState,
): Record<string, GarageLimitOverride> => {
  const config = state.searchRateLimit?.config;
  return config?.countryCode === countryCode ? config.garageOverrides : {};
};

/**
 * Select a specific garage override.
 */
export const selectGarageOverride = (garageId: string, countryCode: string) => (
  state: RootState,
): GarageLimitOverride | undefined => {
  const config = state.searchRateLimit?.config;
  if (config?.countryCode !== countryCode) {
    return undefined;
  }
  return config.garageOverrides?.[garageId];
};

/**
 * Select if a config exists for a country.
 */
export const selectHasConfigForCountry = (countryCode: string) => (state: RootState): boolean => {
  const config = state.searchRateLimit?.config;
  return config?.countryCode === countryCode;
};
